import { useState, useEffect } from 'react';
import api from '../services/api';
import { useNavigate } from 'react-router-dom';

const categoryConfig = {
  informatique: { gradient: 'from-blue-600 to-indigo-700', badge: 'bg-blue-100 text-blue-700' },
  robotique: { gradient: 'from-orange-500 to-red-600', badge: 'bg-orange-100 text-orange-700' },
  data: { gradient: 'from-emerald-500 to-teal-600', badge: 'bg-emerald-100 text-emerald-700' },
  default: { gradient: 'from-purple-600 to-blue-600', badge: 'bg-purple-100 text-purple-700' },
};

const getConfig = (categorie, titre) => {
  const key = ((categorie || '') + ' ' + (titre || '')).toLowerCase();
  if (key.includes('robot')) return categoryConfig.robotique;
  if (key.includes('data')) return categoryConfig.data;
  if (key.includes('info') || key.includes('web') || key.includes('dev') || key.includes('cyber')) return categoryConfig.informatique;
  return categoryConfig.default;
};

const ElearningPage = () => {
  const navigate = useNavigate();
  const [formations, setFormations] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [filtre, setFiltre] = useState('Tous');

  useEffect(() => {
    window.scrollTo(0, 0);
    const load = async () => {
      try {
        const data = await api.get('/formations?mode=vitrine&mode_formation=elearning');
        setFormations(data || []);
      } catch (e) {
        console.error(e);
      } finally {
        setChargement(false);
      }
    };
    load();
  }, []);

  const categories = ['Tous', ...new Set(formations.map(f => f.categorie).filter(Boolean))];
  const filtered = filtre === 'Tous' ? formations : formations.filter(f => f.categorie === filtre);

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-gradient-to-br from-[#1a1f6e] to-[#2d3494] pt-28 pb-16 text-center text-white">
        <p className="text-xs font-bold tracking-widest text-blue-300 uppercase mb-3">E-LEARNING</p>
        <h1 className="text-3xl md:text-4xl font-bold mb-3">Cours en ligne et Certifications</h1>
        <p className="text-blue-200 max-w-xl mx-auto text-sm">Formez-vous avec des experts certifies et obtenez des certificats reconnus.</p>
      </div>

      <div className="flex justify-center gap-3 py-8 px-4 flex-wrap">
        {categories.map(cat => (
          <button key={cat} onClick={() => setFiltre(cat)}
            className={"px-5 py-2 rounded-full text-sm font-medium transition-all " + (filtre === cat ? 'bg-[#1a1f6e] text-white shadow-md' : 'bg-white text-gray-600 border border-gray-200 hover:border-[#1a1f6e] hover:text-[#1a1f6e]')}>
            {cat.charAt(0).toUpperCase() + cat.slice(1)}
          </button>
        ))}
      </div>

      <div className="max-w-6xl mx-auto px-4 pb-20">
        {chargement ? (
          <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1a1f6e]"></div></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 text-gray-400">Aucune formation disponible</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filtered.map(f => {
              const cfg = getConfig(f.categorie, f.titre);
              const prix = f.prix ? parseInt(f.prix).toLocaleString('fr-FR') + ' FCFA' : 'Gratuit';
              return (
                <div key={f.id} className="bg-white rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden flex flex-col group">
                  <div className={"relative h-44 bg-gradient-to-br " + cfg.gradient + " flex items-center justify-center"}>
                    {f.image ? (
                      <img src={"https://pschool.pro/storage/" + f.image} alt={f.titre} className="w-full h-full object-cover" />
                    ) : (
                      <div className="text-white text-6xl font-black opacity-20">{f.titre.charAt(0)}</div>
                    )}
                    <span className={"absolute top-3 left-3 text-xs font-semibold px-3 py-1 rounded-full " + cfg.badge}>{f.categorie || 'Formation'}</span>
                    <span className="absolute top-3 right-3 bg-white text-gray-800 text-xs font-bold px-3 py-1 rounded-full shadow">{prix}</span>
                  </div>
                  <div className="p-5 flex flex-col flex-1">
                    <h3 className="font-bold text-gray-800 text-base mb-2 group-hover:text-[#1a1f6e] transition-colors">{f.titre}</h3>
                    <p className="text-gray-500 text-sm leading-relaxed flex-1">{f.description || 'Decouvrez cette formation.'}</p>
                    <button onClick={() => navigate("/inscription-adulte/" + f.id)}
                      className="mt-4 w-full py-2.5 bg-[#1a1f6e] hover:bg-[#2d3494] text-white text-sm font-semibold rounded-xl transition-all">
                      S inscrire
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="bg-white py-16 px-4 text-center">
        <p className="text-xs font-bold tracking-widest text-[#1a1f6e] uppercase mb-2">PROCESSUS</p>
        <h2 className="text-2xl font-bold text-gray-800 mb-12">Comment ca fonctionne ?</h2>
        <div className="max-w-4xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-6">
          {[{n:'01',t:'Creez votre compte',d:'Inscrivez-vous gratuitement'},{n:'02',t:'Choisissez une formation',d:'Parcourez notre catalogue'},{n:'03',t:'Payez et Apprenez',d:'Acces apres paiement securise'},{n:'04',t:'Obtenez votre certificat',d:'Completez et recevez votre certification'}].map((s,i) => (
            <div key={i} className="bg-slate-50 rounded-2xl p-5 flex flex-col items-center">
              <p className="text-5xl font-black text-slate-100 mb-1">{s.n}</p>
              <h4 className="font-bold text-gray-800 text-sm mb-1">{s.t}</h4>
              <p className="text-gray-500 text-xs">{s.d}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ElearningPage;